import { useEffect, useState } from "react";
import { CREW_ROLES } from "@produce/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GEAR_OPTIONS, type FormState } from "@/lib/teamMembers";

interface TeamMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Non-null when editing an existing member. */
  editingId: string | null;
  /** Form to seed the dialog with when it opens. */
  initialForm: FormState;
  saving: boolean;
  onSubmit: (form: FormState) => void;
}

/**
 * Add / edit a single saved team member. Owns its own form state so the
 * parent page stays thin; seeds from `initialForm` whenever it opens.
 */
export const TeamMemberDialog = ({
  open,
  onOpenChange,
  editingId,
  initialForm,
  saving,
  onSubmit,
}: TeamMemberDialogProps) => {
  const [form, setForm] = useState<FormState>(initialForm);

  useEffect(() => {
    if (open) setForm(initialForm);
  }, [open, initialForm]);

  const toggleGear = (gearType: string) => {
    setForm((prev) => {
      const exists = prev.gear.find((g) => g.type === gearType);
      if (exists) return { ...prev, gear: prev.gear.filter((g) => g.type !== gearType) };
      return { ...prev, gear: [...prev.gear, { type: gearType, detail: "" }] };
    });
  };

  const updateGearDetail = (gearType: string, detail: string) => {
    setForm((prev) => ({
      ...prev,
      gear: prev.gear.map((g) => (g.type === gearType ? { ...g, detail } : g)),
    }));
  };

  const canSave = form.name.trim() && form.email.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editingId ? "Edit team member" : "Add team member"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Full name"
            />
          </div>
          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="email@example.com"
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              placeholder="+31 6..."
            />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {CREW_ROLES.map((r) => (
                  <SelectItem key={r} value={r} className="capitalize">
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Gear</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {GEAR_OPTIONS.map((g) => {
                const active = form.gear.some((gi) => gi.type === g);
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => toggleGear(g)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-transparent text-muted-foreground hover:border-muted-foreground"
                    }`}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
            {form.gear.length > 0 && (
              <div className="mt-3 space-y-2">
                {form.gear.map((g) => (
                  <div key={g.type} className="flex items-center gap-2">
                    <span className="w-24 shrink-0 text-xs text-muted-foreground">{g.type}</span>
                    <Input
                      value={g.detail}
                      onChange={(e) => updateGearDetail(g.type, e.target.value)}
                      placeholder="Specify model/type..."
                      className="h-8 text-sm"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canSave || saving} onClick={() => onSubmit(form)}>
            {saving ? "Saving..." : editingId ? "Update" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TeamMemberDialog;
